import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { InfoOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  IconButton,
  Link,
  Modal,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import PackageAPI from 'api/faber/PackageAPI'
import UsaAPI from 'api/faber/UsaAPI'
import SimOpsAPI, { getSimOpsErrorMessage } from 'api/ops/v2/SimOpsAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import NormalText from 'common/Typography/NormalText'
import { format } from 'date-fns'
import OrbitSession from 'hooks/useCookies'
import {
  GetActiveUserPlansDetailsType,
  GetAllInactiveAndTerminatedOtpackUserPlansDetailsType,
  UserPlanWithPrice
} from 'types/api/faber/oneTimePack.type'

import ReassignPackDialog from '../dialogs/ReassignPackDialog'

import {
  CellText,
  fitTableHeight,
  listGridProps,
  ListSection,
  MonoText,
  MutedText,
  RowActionsMenu,
  StatusPill,
  UsageBar
} from './listUi'

// taller rows fit the "from" link and the usage bar label
const PACK_ROW_HEIGHT = 64

interface Props {
  userPacksUserPlans: UserPlanWithPrice[]
  setOtpackDataRemaining: (value: number) => void
  isExternal: boolean
  userId: string
  pageNumber: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  pageCount: number
  isFetchingOTPackUserPlans: boolean
}

const BYTES_IN_GB = 1073741824

const getUsedData = (row: UserPlanWithPrice) =>
  row.packageDataAllowed - row.dataUsageRemainingInBytes / BYTES_IN_GB

const userPacksColumns = (
  handleReassignPackage: (row: UserPlanWithPrice) => void,
  handleResetPackageActive: (row: UserPlanWithPrice) => void,
  handleResetPackageInactive: (row: UserPlanWithPrice) => void,
  handleTerminatePackageActive: (row: UserPlanWithPrice) => void,
  handleTerminatePackageInactive: (row: UserPlanWithPrice) => void,
  handleShowSelectedPackInfo: (row: UserPlanWithPrice) => void,
  handleViewUser: (row: UserPlanWithPrice) => void,
  handleRefreshPack: (row: UserPlanWithPrice) => void
) => [
  {
    field: 'packageName',
    headerName: 'Pack',
    sortable: false,
    flex: 1.5,
    minWidth: 180,
    renderCell: (params: { row: UserPlanWithPrice }) => (
      <Stack spacing={0.25} minWidth={0}>
        <Stack direction='row' alignItems='center' spacing={0.5} minWidth={0}>
          <CellText strong>{params.row.packageName}</CellText>
          <Tooltip title='Package details'>
            <IconButton
              size='small'
              aria-label='Package details'
              onClick={() => handleShowSelectedPackInfo(params.row)}
              sx={{ p: 0.25, color: 'text.disabled' }}
            >
              <InfoOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
        {params.row.allocatedFrom && (
          <Link
            component='button'
            underline='hover'
            onClick={() => handleViewUser(params.row)}
            sx={{ fontSize: '12px', textAlign: 'left' }}
          >
            From {params.row.allocatedFrom}
          </Link>
        )}
      </Stack>
    )
  },
  {
    field: 'iccid',
    headerName: 'ICCID',
    sortable: false,
    flex: 1.3,
    minWidth: 180,
    renderCell: (params: { row: UserPlanWithPrice }) => (
      <MonoText>{params.row.iccid}</MonoText>
    )
  },
  {
    field: 'dataUsageRemainingInBytes',
    headerName: 'Data Used',
    sortable: false,
    flex: 1.3,
    minWidth: 170,
    renderCell: (params: { row: UserPlanWithPrice }) => {
      // -1 marks an unlimited catalog
      if (Number(params.row.packageDataAllowed) === -1) {
        return <CellText>Unlimited</CellText>
      }
      const used = getUsedData(params.row)
      return (
        <UsageBar
          value={(used / params.row.packageDataAllowed) * 100}
          label={`${parseFloat(used.toFixed(2))} / ${
            params.row.packageDataAllowed
          } GB`}
        />
      )
    }
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    flex: 0.7,
    minWidth: 110,
    renderCell: (params: { row: UserPlanWithPrice }) => (
      <StatusPill status={params.row.planStatus} />
    )
  },
  {
    field: 'packageValidityInDays',
    headerName: 'Validity',
    sortable: false,
    flex: 0.6,
    minWidth: 90,
    renderCell: (params: { row: UserPlanWithPrice }) => (
      <CellText>{params.row.packageValidityInDays} days</CellText>
    )
  },
  {
    field: 'createdAt',
    headerName: 'Purchased',
    sortable: false,
    flex: 0.9,
    minWidth: 140,
    renderCell: (params: { row: UserPlanWithPrice }) => (
      <MutedText>
        {params.row.createdAt
          ? format(new Date(params.row.createdAt), 'dd MMM yyyy, HH:mm')
          : '—'}
      </MutedText>
    )
  },
  {
    field: 'actions',
    headerName: '',
    sortable: false,
    flex: 0,
    width: 56,
    align: 'right' as const,
    renderCell: (params: { row: UserPlanWithPrice }) => {
      const isInactive =
        String(params.row.planStatus || '').toUpperCase() === 'INACTIVE'
      return (
        <RowActionsMenu
          actions={[
            {
              label: 'Refresh status & usage',
              onClick: () => handleRefreshPack(params.row)
            },
            // backend only reassigns INACTIVE plans
            ...(isInactive
              ? [
                  {
                    label: 'Reassign package',
                    onClick: () => handleReassignPackage(params.row)
                  }
                ]
              : []),
            {
              label: 'Reset package',
              onClick: () =>
                isInactive
                  ? handleResetPackageInactive(params.row)
                  : handleResetPackageActive(params.row)
            },
            {
              label: 'Terminate package',
              danger: true,
              onClick: () =>
                isInactive
                  ? handleTerminatePackageInactive(params.row)
                  : handleTerminatePackageActive(params.row)
            }
          ]}
        />
      )
    }
  }
]

export default function CustomerPlansCard({
  userPacksUserPlans,
  setOtpackDataRemaining,
  isExternal,
  userId,
  pageNumber,
  setPage,
  pageCount,
  isFetchingOTPackUserPlans
}: Props) {
  const faberToken: string = OrbitSession.get('faberToken')
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [selectedItem, setSelectedItem] = useState<
    | GetActiveUserPlansDetailsType
    | GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
    | undefined
  >(undefined)
  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false)
  const [showMoreResetPackageInfoModal, setShowMoreResetPackageInfoModal] =
    useState(false)
  const [
    showMoreTerminatePackageInfoModal,
    setShowMoreTerminatePackageInfoModal
  ] = useState(false)
  const [showReassignDialog, setShowReassignDialog] = useState(false)
  const [reassignPlan, setReassignPlan] = useState<UserPlanWithPrice | null>(
    null
  )

  const { data: packageDetails } = useQuery(
    ['getPackageDetails', selectedItem?.appliedPlanId ?? ''],
    async () =>
      isExternal
        ? PackageAPI.getExternalPackageDetails(
            selectedItem?.appliedPlanId ?? '',
            faberToken
          )
        : PackageAPI.getPackageDetails(
            selectedItem?.appliedPlanId ?? '',
            faberToken
          ),
    { enabled: !!selectedItem }
  )
  const packageData = packageDetails?.data.data

  const [selectedPackTotalData, setSelectedPackTotalData] = useState(0)
  const [selectedPackRemainingData, setSelectedPackRemainingData] = useState(0)

  // If more than 100MB, they are not allowed to reset the pack
  // const overResetLimit: boolean = (selectedPackUsedData / (1024 * 1024 * 1024) > 0.1) ? true : false
  let overResetLimit: boolean
  if (
    selectedPackTotalData - selectedPackRemainingData / (1024 * 1024 * 1024) >
    0.1
  ) {
    overResetLimit = true
  } else {
    overResetLimit = false
  }

  const resetPackage = () => {
    const packAssignmentIdRaw = (
      selectedItem as { packAssignmentId?: unknown } | undefined
    )?.packAssignmentId

    if (typeof packAssignmentIdRaw === 'string' && packAssignmentIdRaw) {
      SimOpsAPI.packAction({
        packAssignmentId: packAssignmentIdRaw,
        action: 'reset'
      })
        .then(() => {
          toast.success(
            'Successfully reset package. Please refresh the page to verify the latest status.'
          )
          return
        })
        .catch((error) => {
          toast.error(
            getSimOpsErrorMessage(
              error,
              'Failed and did not reset the package, please contact the engineers'
            )
          )
          return
        })

      return
    }

    toast.error(
      'Unable to reset package as packAssignmentId is missing. Please refresh and try again.'
    )
  }

  const terminatePackage = () => {
    const packAssignmentIdRaw = (
      selectedItem as { packAssignmentId?: unknown } | undefined
    )?.packAssignmentId

    if (typeof packAssignmentIdRaw === 'string' && packAssignmentIdRaw) {
      SimOpsAPI.packAction({
        packAssignmentId: packAssignmentIdRaw,
        action: 'terminate'
      })
        .then(() => {
          toast.success(
            'Successfully terminated package. Please refresh the page to verify the latest status.'
          )
          return
        })
        .catch((error) => {
          toast.error(
            getSimOpsErrorMessage(
              error,
              'Failed and was not able to terminate the package, please contact the engineers'
            )
          )
          return
        })

      return
    }

    toast.error(
      'Unable to terminate package as packAssignmentId is missing. Please refresh and try again.'
    )
  }

  const getUsers = async (user_id: string) => {
    if (user_id.includes('|')) {
      // Global users
      const response = await UsaAPI.getAllGlobalUsers({
        searchCriteria: 'User ID',
        searchValue: user_id,
        faberToken: faberToken
      })
      if (response.status === 200) {
        // Mapping present in FaberGlobalUserSearch.tsx
        return response.data.data.users.map(
          ({ name, family_name, given_name, user_id, user_metadata }) => ({
            name,
            family_name,
            given_name,
            user_id,
            user_metadata,
            login_method: user_id
          })
        )
      }
    } else {
      const response = await OneTimePackApi.getAllFaberUsers({
        searchCriteria: 'Customer ID',
        searchValue: user_id,
        faberToken: faberToken,
        limit: 10,
        planStatus: 'active',
        offset: 0
      })
      if (response.status === 200) {
        // Mapping present in RevampedFaberCustomerDashboard.tsx
        return response.data.body.data.map(
          ({
            _id,
            user_id,
            user_metadata,
            email,
            contactNo,
            address,
            createdAt,
            firstName,
            lastName,
            role,
            showPlanPage,
            showTutorial,
            sin,
            status,
            updatedAt,
            subscription,
            otpack
          }) => ({
            _id,
            user_id,
            user_metadata,
            contactNo,
            firstName,
            email,
            msisdn: contactNo.number,
            address,
            createdAt,
            lastName,
            role,
            showPlanPage,
            showTutorial,
            sin,
            status,
            updatedAt,
            subscription,
            otpack
          })
        )
      }
    }
    return []
  }

  const handleViewUser = async (item) => {
    const userId = item?.allocatedFromID
    const userInfo = await getUsers(userId)
    if (userInfo.length == 1) {
      navigate(`/hq/manage-global-cx/view-profile/${userId}`, {
        state: userInfo[0]
      })
    } else {
      toast.error('Error fetching promocode user profile', {
        toastId: 'error-get-promocode-user-profile'
      })
    }
  }

  const handleReassignPackage = (item: UserPlanWithPrice) => {
    setReassignPlan(item)
    setShowReassignDialog(true)
  }

  const handleResetPackageActive = (item) => {
    setSelectedItem(item)
    setShowMoreResetPackageInfoModal(true)
    setSelectedPackTotalData(item.packageDataAllowed)
    setSelectedPackRemainingData(item.dataUsageRemainingInBytes)
  }

  const handleTerminatePackageActive = (item) => {
    setSelectedItem(item)
    setShowMoreTerminatePackageInfoModal(true)
    setSelectedPackTotalData(item.packageDataAllowed)
    setSelectedPackRemainingData(item.dataUsageRemainingInBytes)
  }

  const handleResetPackageInactive = (item) => {
    setSelectedItem(item)
    setShowMoreResetPackageInfoModal(true)
    setSelectedPackRemainingData(item.packageDataAllowed * 1024 * 1024 * 1024) // remaining data to be in bytes
    setSelectedPackTotalData(item.packageDataAllowed)
  }

  const handleTerminatePackageInactive = (item) => {
    setSelectedItem(item)
    setShowMoreTerminatePackageInfoModal(true)
    setSelectedPackRemainingData(item.packageDataAllowed * 1024 * 1024 * 1024) // remaining data to be in bytes
    setSelectedPackTotalData(item.packageDataAllowed)
  }

  const handleRefreshPack = (item: UserPlanWithPrice) => {
    if (!item.packAssignmentId) {
      toast.error(
        'Unable to refresh package as packAssignmentId is missing. Please refresh and try again.'
      )
      return
    }

    SimOpsAPI.refreshPack(item.packAssignmentId)
      .then(() => {
        toast.success('Package status and usage refreshed')
        queryClient.invalidateQueries(['getAllActiveInactiveOtpackUserPlans'])
      })
      .catch((error) => {
        toast.error(
          getSimOpsErrorMessage(error, 'Failed to refresh the package')
        )
      })
  }

  const handleShowSelectedPackInfo = (item) => {
    setSelectedItem(item)
    setShowMoreInfoModal(true)
  }

  useEffect(() => {
    if (userPacksUserPlans) {
      const totalDataLeft = userPacksUserPlans.reduce(
        (accumulator, item) =>
          accumulator + item.dataUsageRemainingInBytes / 1073741824,
        0
      )
      setOtpackDataRemaining(parseFloat(totalDataLeft.toFixed(2)))
    }
  }, [userPacksUserPlans])

  return (
    <>
      <Stack>
        {/* The information pop up */}
        <Modal
          open={showMoreInfoModal}
          onClose={() => setShowMoreInfoModal(false)}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <Box sx={modalStyle}>
            <Typography variant='h4' gutterBottom>
              Package Details
            </Typography>
            <NormalText>Package ID: {packageData?.packageId}</NormalText>
            <NormalText>
              Package Type ID: {packageData?.packageTypeId}
            </NormalText>
            <NormalText>Status: {packageData?.status}</NormalText>
            <NormalText>
              Data Remaining:{' '}
              {packageData?.dataUsageRemainingInBytes
                ? packageData.dataUsageRemainingInBytes / (1024 * 1024 * 1024) +
                  ' GB'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Created:{' '}
              {packageData?.dateCreated !== undefined
                ? packageData?.dateCreated !== 0
                  ? format(
                      new Date(packageData.dateCreated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Expiry:{' '}
              {packageData?.dateExpiry !== undefined
                ? packageData?.dateExpiry !== 0
                  ? format(
                      new Date(packageData.dateExpiry),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Activated:{' '}
              {packageData?.dateActivated !== undefined
                ? packageData?.dateActivated !== 0
                  ? format(
                      new Date(packageData.dateActivated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Terminated:{' '}
              {packageData?.dateTerminated !== undefined
                ? packageData?.dateTerminated !== 0
                  ? format(
                      new Date(packageData.dateTerminated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
          </Box>
        </Modal>

        {/* The reset information pop up */}
        <Modal
          open={showMoreResetPackageInfoModal}
          onClose={() => setShowMoreResetPackageInfoModal(false)}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <Box sx={modalStyle}>
            <Typography variant='h4' gutterBottom>
              Do you really want to reset the package?
            </Typography>
            <NormalText>Package ID: {packageData?.packageId}</NormalText>
            <NormalText>
              Package Type ID: {packageData?.packageTypeId}
            </NormalText>
            <NormalText>Status: {packageData?.status}</NormalText>

            <NormalText sx={{ color: overResetLimit ? 'red' : 'green' }}>
              {/* Data Remaining:{' '} */}
              {/* {packageData?.dataUsageRemainingInBytes
                ? packageData.dataUsageRemainingInBytes / (1024 * 1024 * 1024) +
                  ' GB'
                : 'N/A'} */}
              {/* Data Used:{' '} */}
              {/* {packageData?.dataUsageRemainingInBytes
                ? 
                (100 - packageData?.dataUsageRemainingInBytes / (1024 * 1024 * 1024) + ' GB')
                : 'N/A'} */}
              Data Used:{' '}
              {
                // parseFloat((selectedPackUsedData / (1024 * 1024 * 1024)).toFixed(2))
                (
                  selectedPackTotalData -
                  selectedPackRemainingData / (1024 * 1024 * 1024)
                ).toFixed(2) + ' GB'
              }
            </NormalText>
            <NormalText>
              Date Created:{' '}
              {packageData?.dateCreated !== undefined
                ? packageData?.dateCreated !== 0
                  ? format(
                      new Date(packageData.dateCreated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Expiry:{' '}
              {packageData?.dateExpiry !== undefined
                ? packageData?.dateExpiry !== 0
                  ? format(
                      new Date(packageData.dateExpiry),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Activated:{' '}
              {packageData?.dateActivated !== undefined
                ? packageData?.dateActivated !== 0
                  ? format(
                      new Date(packageData.dateActivated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Terminated:{' '}
              {packageData?.dateTerminated !== undefined
                ? packageData?.dateTerminated !== 0
                  ? format(
                      new Date(packageData.dateTerminated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>

            <Button
              variant='contained'
              disabled={overResetLimit} // Only allow the button if it is not over the limit
              onClick={() => {
                setShowMoreResetPackageInfoModal(false)
                resetPackage()
              }}
            >
              Yes
            </Button>
            <Button
              onClick={() => {
                setShowMoreResetPackageInfoModal(false)
              }}
            >
              No
            </Button>
          </Box>
        </Modal>

        {/* The terminate information pop up */}
        <Modal
          open={showMoreTerminatePackageInfoModal}
          onClose={() => setShowMoreTerminatePackageInfoModal(false)}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <Box sx={modalStyle}>
            <Typography variant='h4' gutterBottom color='red'>
              Do you really want to terminate the package?
            </Typography>

            <Typography variant='h6' gutterBottom color='red'>
              This will terminate the pack immediately and cannot be undone.
              Please proceed with caution
            </Typography>

            <NormalText>Package ID: {packageData?.packageId}</NormalText>
            <NormalText>
              Package Type ID: {packageData?.packageTypeId}
            </NormalText>
            <NormalText>Status: {packageData?.status}</NormalText>

            {/* No color formatting needed because this is not replacing a package */}
            <NormalText>
              Data Used:{' '}
              {(
                selectedPackTotalData -
                selectedPackRemainingData / (1024 * 1024 * 1024)
              ).toFixed(2) + ' GB'}
            </NormalText>
            <NormalText>
              Date Created:{' '}
              {packageData?.dateCreated !== undefined
                ? packageData?.dateCreated !== 0
                  ? format(
                      new Date(packageData.dateCreated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Expiry:{' '}
              {packageData?.dateExpiry !== undefined
                ? packageData?.dateExpiry !== 0
                  ? format(
                      new Date(packageData.dateExpiry),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Activated:{' '}
              {packageData?.dateActivated !== undefined
                ? packageData?.dateActivated !== 0
                  ? format(
                      new Date(packageData.dateActivated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Terminated:{' '}
              {packageData?.dateTerminated !== undefined
                ? packageData?.dateTerminated !== 0
                  ? format(
                      new Date(packageData.dateTerminated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>

            <Button
              sx={{
                color: 'white',
                background: 'red'
              }}
              variant='contained'
              onClick={() => {
                setShowMoreTerminatePackageInfoModal(false)
                terminatePackage()
              }}
            >
              Yes
            </Button>
            <Button
              onClick={() => {
                setShowMoreTerminatePackageInfoModal(false)
              }}
            >
              No
            </Button>
          </Box>
        </Modal>

        <ReassignPackDialog
          open={showReassignDialog}
          onClose={() => setShowReassignDialog(false)}
          userId={userId}
          packAssignmentId={reassignPlan?.packAssignmentId}
          packageName={reassignPlan?.packageName}
        />

        <ListSection
          title='Active & Inactive Packs'
          description="The customer's current and unused packs."
        >
          <MUIDataGrid
            {...listGridProps}
            columns={userPacksColumns(
              (row) => handleReassignPackage(row),
              (row) => handleResetPackageActive(row),
              (row) => handleResetPackageInactive(row),
              (row) => handleTerminatePackageActive(row),
              (row) => handleTerminatePackageInactive(row),
              (row) => handleShowSelectedPackInfo(row),
              (row) => handleViewUser(row),
              (row) => handleRefreshPack(row)
            )}
            rows={userPacksUserPlans}
            getRowId={(row: UserPlanWithPrice & { id?: string; vendorPlanId?: string }) =>
              row.appliedPlanId || row.userPlanId || row.id || row.vendorPlanId || row.packageName
            }
            rowHeight={PACK_ROW_HEIGHT}
            tableHeight={fitTableHeight(userPacksUserPlans.length, {
              rowHeight: PACK_ROW_HEIGHT,
              maxRows: 10,
              footer: true
            })}
            pageCount={pageCount}
            pageNumber={pageNumber}
            onPageChange={(newPageNumber) => setPage(newPageNumber - 1)}
            disableColumnMenu
            loading={isFetchingOTPackUserPlans}
          />
        </ListSection>
      </Stack>
    </>
  )
}
const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  p: 4
}
